import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Check, Copy } from "lucide-react";
import HVIHeader from "@/components/HVIHeader";
import HVIFooter from "@/components/HVIFooter";
import { buildSeo, SITE_URL, PUBLISHER } from "@/lib/seo";
import { useT } from "@/lib/i18n";
import { useDomTranslate } from "@/hooks/useDomTranslate";

export const Route = createFileRoute("/about")({
  component: AboutPage,
  head: () =>
    buildSeo({
      path: "/about",
      title: "About & Methodology — Hantavirus International",
      description:
        "Who runs the Hantavirus International tracker, how cases are verified, our data sources, editorial standards and how to contact us.",
      image: "/og/about.jpg",
      keywords: [
        "hantavirus methodology",
        "outbreak data verification",
        "editorial transparency",
        "case classification",
        "data sources WHO CDC ECDC",
      ],
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About Hantavirus International",
          url: `${SITE_URL}/about`,
          publisher: PUBLISHER,
        },
      ],
    }),
});

function AboutPage() {
  const { lang } = useT();
  useDomTranslate("#about-article", lang);
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <HVIHeader />
      <main className="flex-1">
        <article id="about-article" className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16 space-y-12">
          <header className="space-y-3">
            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-data">
              About this tracker
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight leading-[1.1]">
              An independent, open-data hantavirus monitor
            </h1>
            <p className="text-[15px] leading-[1.7] text-foreground/80">
              Hantavirus International is an independent, donation-free public-interest
              project that aggregates official outbreak reports into a single live map.
              We are not a government agency, not a health authority and not affiliated
              with WHO, CDC, ECDC or any ministry of health.
            </p>
          </header>

          <Section title="Who runs this">
            <p>
              The site is built and maintained by a small editorial team of independent
              data journalists and OSINT researchers, with technical infrastructure by{" "}
              <a href="https://alphy.studio" target="_blank" rel="noreferrer" className="text-hvi-primary hover:underline">Alphy Studio</a>.
              No single individual is named publicly because the project receives no
              external funding and accepts no advertising-driven editorial influence.
              Editorial decisions are made collectively and documented in our public
              changelog.
            </p>
            <p>
              For press, research collaborations, corrections or institutional inquiries,
              email{" "}
              <a href="mailto:editor@hantavirus.international" className="text-hvi-primary hover:underline">
                editor@hantavirus.international
              </a>.
              We respond to verified journalist and clinician requests within 48 hours.
            </p>
          </Section>

          <Section title="Methodology">
            <p>
              Every case marker on the map originates from at least one named, verifiable
              source. We classify reports into four states:
            </p>
            <ul>
              <li><strong>Confirmed</strong> — laboratory-confirmed by a recognised reference lab (PCR, serology or genome sequencing) and reported by an official authority.</li>
              <li><strong>Suspected</strong> — clinically compatible, under investigation, reported by an official authority but not yet lab-confirmed.</li>
              <li><strong>Monitoring</strong> — exposure events, contact-tracing cohorts and clusters of unexplained illness with epidemiological links.</li>
              <li><strong>Fatal</strong> — deaths attributed to hantavirus disease by the reporting authority.</li>
            </ul>
            <p>
              A case is added only after it appears in (a) an official disease-outbreak
              notification, (b) a peer-reviewed publication, or (c) two independent
              mainstream news outlets citing a named health official. Single-source
              social-media claims are never published.
            </p>
            <p>
              Coordinates are placed at the reported municipality centroid, never at a
              patient's address. When only a country is known, the marker sits at the
              country centroid and is labelled accordingly.
            </p>
          </Section>

          <Section title="Data sources">
            <ul>
              <li>World Health Organization (WHO) — Disease Outbreak News</li>
              <li>US Centers for Disease Control and Prevention (CDC)</li>
              <li>European Centre for Disease Prevention and Control (ECDC)</li>
              <li>UK Health Security Agency (UKHSA)</li>
              <li>Robert Koch Institute (RKI), Germany</li>
              <li>Pan American Health Organization (PAHO) and national ministries</li>
              <li>National Centre for Infectious Diseases (NCID), Singapore</li>
              <li>ProMED-mail moderated outbreak alerts</li>
              <li>Peer-reviewed literature in PubMed, The Lancet and Eurosurveillance</li>
            </ul>
          </Section>

          <Section title="Corrections & retractions">
            <p>
              If you spot a factual error, an outdated case status or a misattributed
              source, email{" "}
              <a href="mailto:corrections@hantavirus.international" className="text-hvi-primary hover:underline">
                corrections@hantavirus.international
              </a>.
              Substantive corrections are logged with a timestamp and a short note on
              what changed and why.
            </p>
          </Section>

          <Section title="What this site is not">
            <ul>
              <li>It is not medical advice. For symptoms or exposure, contact your local health authority.</li>
              <li>It is not a substitute for official surveillance dashboards.</li>
              <li>It does not publish patient identities, addresses or any personally identifiable information.</li>
            </ul>
          </Section>

          <Section title="Embed the live tracker">
            <p>
              Newsrooms, clinical sites and travel publishers can embed a live
              mini-tracker free of charge. The widget pulls case totals and the
              top countries directly from this site and updates automatically.
              Attribution back to{" "}
              <a href="https://hantavirus.international" className="text-hvi-primary hover:underline">hantavirus.international</a>{" "}
              is built in.
            </p>
            <EmbedSnippet />
            <p className="text-sm text-muted-foreground">
              Optional URL parameters: <code className="font-data">?theme=light</code>{" "}
              for a light palette, <code className="font-data">?compact=1</code> for a
              stats-only card (set the iframe height to 160), and{" "}
              <code className="font-data">?limit=10</code> to show up to ten countries.
            </p>
          </Section>
          <div className="pt-6 border-t border-border flex flex-wrap gap-4 text-sm">
            <Link to="/resources" className="text-hvi-primary hover:underline">Clinical resources →</Link>
            <Link to="/" className="text-hvi-primary hover:underline">Back to live map →</Link>
          </div>
        </article>
      </main>
      <HVIFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl md:text-2xl font-semibold tracking-tight">{title}</h2>
      <div className="text-[15px] leading-[1.75] text-foreground/80 space-y-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:marker:text-muted-foreground [&_strong]:text-foreground [&_strong]:font-semibold">
        {children}
      </div>
    </section>
  );
}

function EmbedSnippet() {
  const snippet = `<iframe
  src="${SITE_URL}/embed"
  width="100%"
  height="360"
  style="border:0;max-width:480px"
  loading="lazy"
  title="Hantavirus live tracker"
  referrerpolicy="no-referrer-when-downgrade"
></iframe>`;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* noop */
    }
  };
  return (
    <div className="border border-border bg-card/40 rounded">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Iframe snippet</span>
        <button
          onClick={copy}
          className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold border px-2 py-1 transition ${
            copied ? "border-hvi-primary text-hvi-primary" : "border-border text-muted-foreground hover:border-hvi-primary hover:text-hvi-primary"
          }`}
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="px-3 py-3 text-[12px] leading-relaxed text-foreground/90 font-data overflow-x-auto whitespace-pre">{snippet}</pre>
    </div>
  );
}
